import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODULES } from '@/config/modules.config';
import { isModuleEnabled } from '@/config/featureFlags';
import { useI18n } from '@/i18n';
import { dataService } from '@/services/dataService';

/**
 * GLOBAL COMMAND PALETTE + SEARCH SHELL.
 *
 * Three rules that make this genuinely modular instead of hardcoded to
 * Creators (per the brief's explicit instruction):
 *  1. "Go to X" navigation commands are generated FROM MODULES — a future
 *     module gets a nav command automatically, no palette edit.
 *  2. "New X" create commands are generated from a small explicit list
 *     (CREATE_COMMANDS) that reuses the SAME `?new=1` deep-link convention
 *     every list page already listens for — adding a creatable entity
 *     means one entry here, not new plumbing.
 *  3. Entity search below is a small, explicitly-listed set of
 *     dataService calls. Adding a searchable entity means adding one
 *     entry to the search Promise.all, not touching rendering logic.
 *
 * Reset Demo Data deliberately does NOT run here — it navigates to
 * /settings, where the real ConfirmModal-gated buttons live. A one-click
 * destructive action from the palette would violate the brief's own
 * "never one-click destructive, always confirm" rule.
 */

interface SearchGroup {
  entity: string;
  labelKey: string;
  results: { id: string; label: string; route: string }[];
}

interface PaletteCommand {
  id: string;
  label: string;
  run: () => void;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState<SearchGroup[]>([]);

  const navCommands: PaletteCommand[] = useMemo(
    () =>
      MODULES.filter((m) => m.group === 'live' && isModuleEnabled(m.featureFlag)).map((m) => ({
        id: `nav-${m.id}`,
        label: `${t('palette.goTo')} ${t(m.labelKey)}`,
        run: () => navigate(m.route),
      })),
    [t, navigate]
  );

  const createCommands: PaletteCommand[] = useMemo(
    () => [
      { id: 'new-creator', label: `${t('palette.new')} ${t('quickCreate.creator')}`, run: () => navigate('/creators?new=1') },
      { id: 'new-followup', label: `${t('palette.new')} ${t('quickCreate.followup')}`, run: () => navigate('/creators?new=1') },
      { id: 'new-partnership', label: `${t('palette.new')} ${t('quickCreate.partnership')}`, run: () => navigate('/partnerships?new=1') },
      { id: 'new-contract', label: `${t('palette.new')} ${t('quickCreate.contract')}`, run: () => navigate('/contracts?new=1') },
      { id: 'new-agent', label: `${t('palette.new')} ${t('quickCreate.agent')}`, run: () => navigate('/agents?new=1') },
      { id: 'new-automation', label: `${t('palette.new')} ${t('quickCreate.automation')}`, run: () => navigate('/automations?new=1') },
      { id: 'new-memory', label: `${t('palette.new')} ${t('quickCreate.memory')}`, run: () => navigate('/memory?new=1') },
    ],
    [t, navigate]
  );

  const actionCommands: PaletteCommand[] = useMemo(
    () => [
      { id: 'toggle-theme', label: t('palette.toggleTheme'), run: () => document.dispatchEvent(new CustomEvent('ugarits:toggle-theme')) },
      { id: 'print-page', label: t('palette.printPage'), run: () => window.print() },
      { id: 'reset-demo', label: t('palette.resetDemoData'), run: () => navigate('/settings') },
      { id: 'run-diagnostics', label: t('palette.runDiagnostics'), run: () => navigate('/support') },
    ],
    [t, navigate]
  );

  const allCommands = useMemo(() => [...navCommands, ...createCommands, ...actionCommands], [navCommands, createCommands, actionCommands]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setGroups([]);
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      setGroups([]);
      return;
    }
    const q = query.trim().toLowerCase();
    let cancelled = false;
    Promise.all([
      dataService.listCreators(),
      dataService.listPartnerships(),
      dataService.listContracts(),
      dataService.listApprovals(),
      dataService.listAgents(),
      dataService.listAutomations(),
      dataService.listMemory(),
    ]).then(([creators, partnerships, contracts, approvals, agents, automations, memoryEntries]) => {
      if (cancelled) return;
      const next: SearchGroup[] = [];

      const creatorMatches = creators
        .filter((c) => c.id.toLowerCase().includes(q) || c.fullName.toLowerCase().includes(q) || c.tiktokUsername.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.phone.toLowerCase().includes(q))
        .map((c) => ({ id: c.id, label: `${c.id} — ${c.fullName}`, route: `/creators/${c.id}` }));
      if (creatorMatches.length) next.push({ entity: 'creators', labelKey: 'nav.creators', results: creatorMatches });

      const partnerMatches = partnerships
        .filter((p) => p.id.toLowerCase().includes(q) || p.partnerName.toLowerCase().includes(q))
        .map((p) => ({ id: p.id, label: `${p.id} — ${p.partnerName}`, route: `/partnerships/${p.id}` }));
      if (partnerMatches.length) next.push({ entity: 'partnerships', labelKey: 'nav.partnerships', results: partnerMatches });

      const contractMatches = contracts
        .filter((c) => c.id.toLowerCase().includes(q) || c.title.toLowerCase().includes(q))
        .map((c) => ({ id: c.id, label: `${c.id} — ${c.title}`, route: `/contracts` }));
      if (contractMatches.length) next.push({ entity: 'contracts', labelKey: 'nav.contracts', results: contractMatches });

      const approvalMatches = approvals
        .filter((a) => a.id.toLowerCase().includes(q) || a.actionTitle.toLowerCase().includes(q))
        .map((a) => ({ id: a.id, label: `${a.id} — ${a.actionTitle}`, route: `/approvals` }));
      if (approvalMatches.length) next.push({ entity: 'approvals', labelKey: 'nav.approvals', results: approvalMatches });

      const agentMatches = agents
        .filter((a) => a.id.toLowerCase().includes(q) || a.name.toLowerCase().includes(q))
        .map((a) => ({ id: a.id, label: `${a.id} — ${a.name}`, route: `/agents` }));
      if (agentMatches.length) next.push({ entity: 'agents', labelKey: 'nav.agents', results: agentMatches });

      const automationMatches = automations
        .filter((a) => a.id.toLowerCase().includes(q) || a.name.toLowerCase().includes(q))
        .map((a) => ({ id: a.id, label: `${a.id} — ${a.name}`, route: `/automations` }));
      if (automationMatches.length) next.push({ entity: 'automations', labelKey: 'nav.automations', results: automationMatches });

      const memoryMatches = memoryEntries
        .filter((m) => m.id.toLowerCase().includes(q) || m.title.toLowerCase().includes(q))
        .map((m) => ({ id: m.id, label: `${m.id} — ${m.title}`, route: `/memory` }));
      if (memoryMatches.length) next.push({ entity: 'memory', labelKey: 'nav.memory', results: memoryMatches });

      setGroups(next);
    });
    return () => {
      cancelled = true;
    };
  }, [query]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (open) onClose();
        else document.dispatchEvent(new CustomEvent('ugarits:open-palette'));
      }
      if (e.key === 'Escape' && open) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const filteredCommands = query ? allCommands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase())) : [];

  function runCommand(cmd: PaletteCommand) {
    cmd.run();
    onClose();
  }

  return (
    <div className="palette-backdrop" onClick={onClose}>
      <div className="palette-panel" onClick={(e) => e.stopPropagation()}>
        <input
          autoFocus
          className="palette-input"
          placeholder={t('palette.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="palette-results">
          {!query && (
            <>
              <div className="palette-group">
                <div className="palette-group-label">{t('palette.goTo')}</div>
                {navCommands.map((c) => (
                  <div key={c.id} className="palette-item" onClick={() => runCommand(c)}>
                    {c.label}
                  </div>
                ))}
              </div>
              <div className="palette-group">
                <div className="palette-group-label">{t('palette.create')}</div>
                {createCommands.map((c) => (
                  <div key={c.id} className="palette-item" onClick={() => runCommand(c)}>
                    {c.label}
                  </div>
                ))}
              </div>
              <div className="palette-group">
                <div className="palette-group-label">{t('palette.actions')}</div>
                {actionCommands.map((c) => (
                  <div key={c.id} className="palette-item" onClick={() => runCommand(c)}>
                    {c.label}
                  </div>
                ))}
              </div>
            </>
          )}
          {query && filteredCommands.length > 0 && (
            <div className="palette-group">
              {filteredCommands.map((c) => (
                <div key={c.id} className="palette-item" onClick={() => runCommand(c)}>
                  {c.label}
                </div>
              ))}
            </div>
          )}
          {query &&
            groups.map((g) => (
              <div key={g.entity} className="palette-group">
                <div className="palette-group-label">{t(g.labelKey)}</div>
                {g.results.map((r) => (
                  <div key={r.id} className="palette-item" onClick={() => { navigate(r.route); onClose(); }}>
                    {r.label}
                  </div>
                ))}
              </div>
            ))}
          {query && filteredCommands.length === 0 && groups.length === 0 && (
            <div className="palette-empty">{t('palette.noResults')}</div>
          )}
        </div>
        <div className="palette-hint">{t('palette.hint')}</div>
      </div>
    </div>
  );
}
